/** @jsxImportSource @emotion/react */
import { Order } from "../../model/OrderSuccessResponse";
import * as authSelectors from "../../../auth/authSelectors";
import { CardContainer } from "../../../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../../../components/page-skeleton/PageHeader";
import { Divider } from "../../../../components/Divider";
import { css } from "@emotion/react";
import { CardBody } from "../../../../components/page-skeleton/CardBody";
import { StyledText } from "../../../../assets/typography/typography";
import { formatDateWithYearAndTime, formatMoneyAmount } from "../../../../util";
import { colors } from "../../../../components/colors";
import { EllipsisDropdown } from "../../../../components/EllipsisDropdown";
import { DropdownItemButton } from "../../../common/DropdownItemButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { RefundOrderConfirmModal } from "./RefundOrderConfirmModal";
import React, { useState } from "react";
import { MenuProps } from "antd";
import { SmallBadge } from "../../../../components/SmallBadge";
import { useRefundGiftCard } from "../service/order-api";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../../error-handler";

type Props = {
  order: Order;
};

const getPaymentFacilitator = (order: Order) => {
  switch (order.paymentFacilitator) {
    case "heaps_go":
      return "HeapsGo";
    case "wolt":
      return "Wolt";
    case "just_eat":
      return "Just Eat";
    case "online_pos":
      return "Online POS";
    default:
      return order.paymentFacilitator;
  }
};

/* 
Calculated by subtracting refunds from the total.
*/
const getTotalPaidAmount = (order: Order) => {
  const price = {
    amount: order.total.amount,
    currency: order.total.currency,
  };

  if (order.refunds && order.refunds.length > 0) {
    price.amount -= order.refunds.reduce(
      (acc, refund) => acc + refund.amount.amount,
      0
    );
  }

  if (order.gift_cards && order.gift_cards.length > 0) {
    price.amount -= order.gift_cards
      .filter((gc) => gc.status === "refunded")
      .reduce((acc, card) => acc + card.valueRedeemed.amount, 0);
  }

  return price;
};

/* 
    Calculated by subtracting gift card value redeemed from the total amount.
    Showing what is paid with main payment method.
  */
const getPaymentAmount = (order: Order) => {
  const price = {
    amount: order.total.amount,
    currency: order.total.currency,
  };

  if (order.gift_cards && order.gift_cards.length > 0) {
    price.amount -= order.gift_cards.reduce(
      (acc, card) => acc + card.valueRedeemed.amount,
      0
    );
  }

  return price;
};

const PaymentRow = ({
  leftElement,
  middleElement,
  rightElement,
}: {
  leftElement: React.ReactNode;
  middleElement: React.ReactNode;
  rightElement: React.ReactNode;
}) => {
  const paymentWrapperStyle = css`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 16px 0;
  `;

  const paymentItemStyle = css`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
  `;

  const leftPaymentItemStyle = css`
    ${paymentItemStyle}
    justify-content: start;
  `;

  const middlePaymentItemStyle = css`
    ${paymentItemStyle}
    justify-content: end;
  `;

  const rightPaymentItemStyle = css`
    ${paymentItemStyle}
    justify-content: end;
    gap: 8px;
  `;

  const leftItemContentStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  `;

  return (
    <div css={paymentWrapperStyle}>
      <div css={leftPaymentItemStyle}>
        <div css={leftItemContentStyle}>{leftElement}</div>
      </div>
      <div css={middlePaymentItemStyle}>{middleElement}</div>
      <div css={rightPaymentItemStyle}>{rightElement}</div>
    </div>
  );
};

const MainPaymentRow = ({
  order,
  setShowRefund,
}: {
  order: Order;
  setShowRefund: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const isRefundable =
    order?.actions.includes("refundable") && order.paymentMethod !== "NETS";

  const dropdownMenu: MenuProps = {
    items: [
      {
        key: "1",
        label: (
          <DropdownItemButton
            onClick={() => {
              setShowRefund(true);
            }}
            type="button"
            icon={<FontAwesomeIcon icon={solid("rotate")} />}
          >
            Refund payment
          </DropdownItemButton>
        ),
      },
    ],
  };

  const getLeftElementSubtext = () => {
    if (order.paymentFacilitator === "online_pos") {
      return `No payment received in HeapsGo. Payment should be handled in ${getPaymentFacilitator(
        order
      )}.`;
    }

    return "";
  };

  const leftContainerStyle = css`
    display: flex;
    flex-direction: column;
    align-items: start;
    ${getLeftElementSubtext() && `gap: 8px;`}
  `;

  return (
    <CardBody>
      <PaymentRow
        leftElement={
          <div css={leftContainerStyle}>
            <StyledText
              className="bodyTextRegular mb-0"
              color={colors.textPrimary}
            >
              {order?.paymentMethod || getPaymentFacilitator(order)}
            </StyledText>
            <StyledText
              className="bodySubtextRegular mb-0"
              color={colors.textSecondary}
            >
              {getLeftElementSubtext()}
            </StyledText>
          </div>
        }
        middleElement={
          <StyledText
            className="bodyTextRegular mb-0"
            color={colors.textSecondary}
          >
            {order?.placedAt ? formatDateWithYearAndTime(order?.placedAt) : "-"}
          </StyledText>
        }
        rightElement={
          <>
            <StyledText
              className="bodyTextRegular mb-0"
              color={colors.textSecondary}
            >
              {formatMoneyAmount(
                getPaymentAmount(order).amount,
                getPaymentAmount(order).currency
              )}
            </StyledText>
            {isRefundable && <EllipsisDropdown menu={dropdownMenu} />}
          </>
        }
      />
    </CardBody>
  );
};

const RefundRow = ({
  priceRefunded,
  date,
}: {
  priceRefunded: { amount: number; currency: string };
  date: string;
}) => {
  const leftElementStyle = css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  `;

  const cardAdditionalStyles = css`
    background-color: ${colors.whiteHover};
  `;

  return (
    <CardBody additionalStyles={cardAdditionalStyles}>
      <PaymentRow
        leftElement={
          <div css={leftElementStyle}>
            <StyledText
              className="bodyTextRegular mb-0"
              color={colors.textPrimary}
            >
              <FontAwesomeIcon icon={solid("turn-down-right")} />
            </StyledText>
            <StyledText
              className="bodyTextRegular mb-0"
              color={colors.textPrimary}
            >
              Refund
            </StyledText>
          </div>
        }
        middleElement={
          <StyledText
            className="bodyTextRegular mb-0"
            color={colors.textSecondary}
          >
            {date}
          </StyledText>
        }
        rightElement={
          <StyledText
            className="bodyTextRegular mb-0"
            color={colors.textSecondary}
          >
            - {formatMoneyAmount(priceRefunded.amount, priceRefunded.currency)}
          </StyledText>
        }
      />
    </CardBody>
  );
};

const RefundsRows = ({ order }: { order: Order }) => {
  return (
    <>
      {order.refunds?.map((refund) => (
        <>
          <RefundRow
            date={
              refund.refundedAt
                ? formatDateWithYearAndTime(refund.refundedAt)
                : "-"
            }
            priceRefunded={refund.amount}
          />
          <Divider />
        </>
      ))}
    </>
  );
};

const CancelledRow = ({ order }: { order: Order }) => {
  const leftElementStyle = css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  `;

  const cardAdditionalStyles = css`
    background-color: ${colors.whiteHover};
  `;

  return (
    <CardBody additionalStyles={cardAdditionalStyles}>
      <PaymentRow
        leftElement={
          <div css={leftElementStyle}>
            <StyledText
              className="bodyTextRegular mb-0"
              color={colors.textPrimary}
            >
              <FontAwesomeIcon icon={solid("turn-down-right")} />
            </StyledText>
            <StyledText
              className="bodyTextRegular mb-0"
              color={colors.textPrimary}
            >
              Cancelled
            </StyledText>
          </div>
        }
        middleElement={
          <StyledText
            className="bodyTextRegular mb-0"
            color={colors.textSecondary}
          >
            -
          </StyledText>
        }
        rightElement={
          <StyledText
            className="bodyTextRegular mb-0"
            color={colors.textSecondary}
          >
            -{" "}
            {formatMoneyAmount(
              getTotalPaidAmount(order).amount,
              getTotalPaidAmount(order).currency
            )}
          </StyledText>
        }
      />
    </CardBody>
  );
};

const GiftcardRows = ({ order }: { order: Order }) => {
  const organization = useSelector(authSelectors.selectedOrganizationKeyName);

  const { mutateAsync: refundGiftcard } = useRefundGiftCard(order.id);

  const dropdownMenu: MenuProps = {
    items: [
      {
        key: "1",
        label: (
          <DropdownItemButton
            onClick={async () => {
              try {
                await refundGiftcard({
                  organizationKeyName: organization,
                  orderId: order.id,
                });
                toastr.success("Success", "Gift card refunded successfully");
              } catch (error: unknown) {
                const errorMessage = getErrorMessage(error);
                toastr.error("Error", errorMessage);
              }
            }}
            type="button"
            icon={<FontAwesomeIcon icon={solid("rotate")} />}
          >
            Refund gift card
          </DropdownItemButton>
        ),
      },
    ],
  };

  return (
    <>
      {order.gift_cards?.map((giftCard, index) => (
        <React.Fragment key={index}>
          <CardBody>
            <PaymentRow
              leftElement={
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {`Gift card (${giftCard.code})`}
                </StyledText>
              }
              middleElement={
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textSecondary}
                >
                  {order.placedAt
                    ? formatDateWithYearAndTime(order.placedAt)
                    : "-"}
                </StyledText>
              }
              rightElement={
                <>
                  <StyledText
                    className="bodyTextRegular mb-0"
                    color={colors.textSecondary}
                  >
                    {formatMoneyAmount(
                      giftCard.valueRedeemed.amount,
                      giftCard.valueRedeemed.currency
                    )}
                  </StyledText>
                  {giftCard.status !== "refunded" && (
                    <EllipsisDropdown menu={dropdownMenu} />
                  )}
                </>
              }
            />
          </CardBody>
          {giftCard.status === "refunded" && (
            <>
              <Divider />
              <RefundRow date="-" priceRefunded={giftCard.valueRedeemed} />
            </>
          )}
          <Divider />
        </React.Fragment>
      ))}
    </>
  );
};

const TotalRow = ({
  order,
  orderIsNotPaid,
}: {
  order: Order;
  orderIsNotPaid: boolean;
}) => {
  const totalContainerStyle = css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  `;

  return (
    <CardBody>
      <div
        css={[
          totalContainerStyle,
          css`
            padding-bottom: 16px;
            padding-top: 16px;
          `,
        ]}
      >
        <StyledText
          className="bodyTextSemibold mb-0"
          color={colors.textPrimary}
        >
          Total paid by customer
        </StyledText>
        <StyledText
          className="bodyTextSemibold mb-0"
          color={colors.textPrimary}
        >
          {formatMoneyAmount(
            orderIsNotPaid ? 0 : getTotalPaidAmount(order).amount,
            getTotalPaidAmount(order).currency
          )}
        </StyledText>
      </div>
    </CardBody>
  );
};

const CardRightHeaderElement = ({ order }: { order: Order }) => {
  const getBadgeBorderColor = () => {
    const facilitator = order.paymentFacilitator;

    if (facilitator === "heaps_go") {
      return colors.blueMedium;
    }

    return colors.orangeMedium;
  };

  const getBadgeBackgroundColor = () => {
    const facilitator = order.paymentFacilitator;

    if (facilitator === "heaps_go") {
      return colors.blueLight;
    }

    return colors.orangeLight;
  };

  const getBadgeTextColor = () => {
    const facilitator = order.paymentFacilitator;

    if (facilitator === "heaps_go") {
      return colors.blueDark;
    }

    return colors.organgeDark;
  };

  return (
    <SmallBadge
      backgroundColor={getBadgeBackgroundColor()}
      borderColor={getBadgeBorderColor()}
    >
      <StyledText className="bodyTextRegular mb-0" color={getBadgeTextColor()}>
        {getPaymentFacilitator(order)}
      </StyledText>
    </SmallBadge>
  );
};

export const OrderPayments = ({ order }: Props) => {
  const [showRefund, setShowRefund] = useState(false);

  const orderIsNotPaid =
    !order.paymentMethod &&
    order.paymentFacilitator === "heaps_go" &&
    order.total.amount > 0;

  return (
    <CardContainer>
      <PageHeader title="Payments">
        <CardRightHeaderElement order={order} />
      </PageHeader>
      <div>
        <Divider />
        {getPaymentAmount(order).amount > 0 && (
          <>
            <MainPaymentRow order={order} setShowRefund={setShowRefund} />
            <Divider />
          </>
        )}
        {orderIsNotPaid && (
          <>
            <CancelledRow order={order} />
            <Divider />
          </>
        )}
        <RefundsRows order={order} />
        <GiftcardRows order={order} />
        <TotalRow order={order} orderIsNotPaid={orderIsNotPaid} />
        <RefundOrderConfirmModal
          isOpen={showRefund}
          order={order}
          closeModal={() => setShowRefund(false)}
        />
      </div>
    </CardContainer>
  );
};
